<script>
export default {
  name: 'MasterSkuRegisterGuide',
  data() {
    return {
      activeTab: 'overview',
      tabs: [
        { name: 'overview', label: '화면 개요' },
        { name: 'single', label: '단건 등록' },
        { name: 'excel', label: '엑셀 등록' },
        { name: 'same-sku', label: '동일 SKU 기준' },
        { name: 'packing', label: '포장 중 등록·연결' },
      ]
    }
  }
}
</script>

<template>
  <div class="register-guide">
    <q-tabs
      v-model="activeTab"
      align="left"
      active-color="primary"
      indicator-color="primary"
      dense
      class="register-guide__tabs"
    >
      <q-tab v-for="tab in tabs" :key="tab.name" :name="tab.name" :label="tab.label" />
    </q-tabs>

    <q-separator />

    <q-tab-panels v-model="activeTab" animated class="register-guide__panels">
      <q-tab-panel name="packing">
        <div class="guide-heading">포장 중 상품 등록·연결</div>
        <p class="guide-copy">포장 화면에서 선택한 주문의 상품을 조회합니다. 공통 상품과 파트너 연결이 모두 없을 때만 미등록 확인창을 열며, 확인만으로 DB에 저장하지 않습니다.</p>
        <ol class="guide-steps">
          <li><span>1</span><div><b>조회 결과를 구분합니다.</b><small>미등록은 신규 상품 등록, 기존 상품은 파트너 연결, 필수정보 부족은 보완으로 처리합니다. 조회 실패 시 다시 조회합니다.</small></div></li>
          <li><span>2</span><div><b>관리자가 별도로 저장합니다.</b><small>바코드와 현재 파트너를 고정하고 기존 등록 규칙의 상품명·브랜드·카테고리·HS CODE·규격 정보를 입력합니다. 익스프레스에는 양의 중량이 필요합니다.</small></div></li>
          <li><span>3</span><div><b>같은 주문에서 이어갑니다.</b><small>저장 후 상품과 주문을 다시 조회하고 중단 단계만 재개합니다. 취소·실패 시 주문과 스캔 수량을 유지하며 ‘상품 확인 후 재개’를 사용할 수 있습니다.</small></div></li>
        </ol>
        <p class="guide-copy">일반·익스프레스 상품 처리 중 추가 스캔은 보관하지 않습니다. 현재 결과 반영 후 2초가 지나면 다음 상품을 다시 스캔하세요. HBL 확인 스캔에는 이 대기 시간을 적용하지 않습니다. 수량 저장 결과가 불명확하면 관리자 확인 전까지 재스캔하지 마세요.</p>
        <q-banner class="guide-warning">공통 상품 변경은 다른 파트너에도 적용됩니다. 파트너 사용자는 관리자에게 요청해야 합니다. 출력 접수와 실물 출력은 다르며 출력 결과 불명확 시 자동 재출력하지 않습니다.</q-banner>
      </q-tab-panel>
      <q-tab-panel name="overview">
        <div class="guide-heading">신규 등록 화면에서 하는 일</div>
        <p class="guide-copy">
          관리자가 여러 파트너가 공통으로 연결할 수 있는 기준 상품을 직접 등록합니다.
          등록 전에 목록 조회에서 동일한 기준 상품이 이미 있는지 먼저 확인하세요.
        </p>

        <div class="row q-col-gutter-md q-mt-sm">
          <div class="col-12 col-md-4">
            <div class="guide-card">
              <q-icon name="search" color="primary" size="24px" />
              <div class="guide-card__title">1. 기존 상품 확인</div>
              <div class="guide-card__copy">목록 조회에서 브랜드, 카테고리, 규격 조건으로 기존 상품을 찾습니다.</div>
            </div>
          </div>
          <div class="col-12 col-md-4">
            <div class="guide-card">
              <q-icon name="rule" color="primary" size="24px" />
              <div class="guide-card__title">2. 등록 방식 선택</div>
              <div class="guide-card__copy">상품 수가 적으면 단건 등록, 여러 건이면 엑셀 등록을 사용합니다.</div>
            </div>
          </div>
          <div class="col-12 col-md-4">
            <div class="guide-card">
              <q-icon name="task_alt" color="primary" size="24px" />
              <div class="guide-card__title">3. 결과 확인</div>
              <div class="guide-card__copy">등록 완료 후 상세 화면 또는 목록 조회에서 결과를 확인합니다.</div>
            </div>
          </div>
        </div>

        <q-banner rounded class="guide-warning q-mt-md">
          <template #avatar>
            <q-icon name="info" color="orange-8" />
          </template>
          동일한 기준 상품을 중복 생성하지 않도록 신규 등록 전에 목록 조회를 사용하세요.
        </q-banner>
      </q-tab-panel>

      <q-tab-panel name="single">
        <div class="guide-heading">단건 등록 사용 순서</div>
        <p class="guide-copy">상품 1~2개를 빠르게 직접 등록할 때 사용합니다.</p>
        <ol class="guide-steps">
          <li><span>1</span><div><b>단건 등록 탭을 선택합니다.</b><small>화면 진입 시 기본으로 선택되어 있습니다.</small></div></li>
          <li><span>2</span><div><b>상품 기본 정보를 입력합니다.</b><small>상품명, 브랜드, 카테고리, HS CODE, 중량, 부피, 가로/세로/높이를 입력합니다.</small></div></li>
          <li><span>3</span><div><b>규격 타입과 규격 상세 값을 입력합니다.</b><small>APPAREL_SIZE, PRODUCT_OPTION, PACK_COUNT 중 하나를 선택합니다.</small></div></li>
          <li><span>4</span><div><b>타이틀 우측의 저장 버튼을 누릅니다.</b><small>등록 완료 후 생성된 상품의 상세 화면으로 이동합니다.</small></div></li>
        </ol>
      </q-tab-panel>

      <q-tab-panel name="excel">
        <div class="guide-heading">엑셀 등록 사용 순서</div>
        <p class="guide-copy">여러 상품을 한 번에 등록할 때 사용합니다. 파일 첨부 즉시 저장되지는 않습니다.</p>
        <ol class="guide-steps">
          <li><span>1</span><div><b>엑셀 등록 탭을 선택합니다.</b><small>타이틀 우측에 양식 다운로드 버튼이 표시됩니다.</small></div></li>
          <li><span>2</span><div><b>양식을 다운로드하고 상품 정보를 작성합니다.</b><small>엑셀 파일의 열 구조는 변경하지 마세요.</small></div></li>
          <li><span>3</span><div><b>파일 선택 후 미리보기를 실행합니다.</b><small>업로드 데이터 검토 테이블에서 내용을 확인합니다.</small></div></li>
          <li><span>4</span><div><b>오류가 있는 셀을 직접 수정합니다.</b><small>수정 후에는 반드시 재검증을 실행하세요.</small></div></li>
          <li><span>5</span><div><b>수정 필요 건수가 0건이면 저장합니다.</b><small>검증을 통과한 상품만 등록합니다.</small></div></li>
        </ol>
        <q-banner rounded class="guide-warning q-mt-md">
          <template #avatar>
            <q-icon name="warning_amber" color="orange-8" />
          </template>
          엑셀 등록은 미리보기 → 수정 → 재검증 → 저장 순서로 진행합니다.
        </q-banner>
      </q-tab-panel>

      <q-tab-panel name="same-sku">
        <div class="guide-heading">동일 SKU 판단 기준</div>
        <p class="guide-copy">아래 다섯 가지 조건이 모두 같으면 동일한 기준 상품으로 판단합니다.</p>
        <div class="same-sku-grid q-mt-md">
          <q-chip color="teal-1" text-color="teal-9">브랜드</q-chip>
          <q-chip color="teal-1" text-color="teal-9">카테고리</q-chip>
          <q-chip color="teal-1" text-color="teal-9">HS CODE</q-chip>
          <q-chip color="teal-1" text-color="teal-9">규격 타입</q-chip>
          <q-chip color="teal-1" text-color="teal-9">규격 상세 값</q-chip>
        </div>
        <q-banner rounded class="guide-warning q-mt-md">
          <template #avatar>
            <q-icon name="info" color="orange-8" />
          </template>
          상품명만 같다고 동일 SKU로 판단하지 않습니다. 규격 조건까지 함께 확인하세요.
        </q-banner>
      </q-tab-panel>
    </q-tab-panels>
  </div>
</template>

<style scoped>
.register-guide__tabs {
  background: #f8fafc;
}

.register-guide__panels {
  background: #fff;
}

.guide-heading {
  color: #183b56;
  font-size: 20px;
  font-weight: 700;
}

.guide-copy {
  margin: 8px 0 0;
  color: #607171;
}

.guide-card {
  height: 100%;
  padding: 16px;
  border: 1px solid #d8e1df;
  border-radius: 12px;
  background: #fff;
}

.guide-card__title {
  margin-top: 10px;
  color: #183b56;
  font-weight: 700;
}

.guide-card__copy {
  margin-top: 5px;
  color: #607171;
  font-size: 13px;
}

.guide-warning {
  color: #8b581d;
  background: #fff8e8;
}

.guide-steps {
  display: grid;
  gap: 14px;
  margin: 20px 0 0;
  padding: 0;
  list-style: none;
}

.guide-steps li {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.guide-steps li > span {
  display: inline-flex;
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #fff;
  background: #1976d2;
  font-size: 12px;
  font-weight: 700;
}

.guide-steps b,
.guide-steps small {
  display: block;
}

.guide-steps b {
  color: #183b56;
}

.guide-steps small {
  margin-top: 2px;
  color: #607171;
  font-size: 13px;
}

.same-sku-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
</style>
